import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, TrendingUp, Zap, CheckCircle2 } from 'lucide-react';

interface GenerationStats {
  sequencesGenerated: number;
  largestNumber: string;
  totalTime: number;
  averageTime: number;
  lastMethod: string;
}

interface FibonacciResult {
  sequence: string[];
  method: string;
  count: number;
  processingTime: number;
  timestamp: Date;
  ratios?: number[];
  properties?: string[];
}

interface FibonacciProperties {
  isPrime: boolean;
  isEven: boolean;
  isPerfectSquare: boolean;
  digitalRoot: number;
  sumOfDigits: number;
}

const FibonacciGeneratorTool: React.FC = () => {
  const [count, setCount] = useState<string>('10');
  const [maxValue, setMaxValue] = useState<string>('');
  const [generationMethod, setGenerationMethod] = useState<'count' | 'max-value' | 'nth-term'>('count');
  const [startValues, setStartValues] = useState<{ a: string; b: string }>({ a: '0', b: '1' });
  const [customStart, setCustomStart] = useState<boolean>(false);
  const [showProperties, setShowProperties] = useState<boolean>(false);
  const [showRatios, setShowRatios] = useState<boolean>(false);
  const [result, setResult] = useState<FibonacciResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<GenerationStats>({
    sequencesGenerated: 0,
    largestNumber: '0',
    totalTime: 0,
    averageTime: 0,
    lastMethod: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<FibonacciResult[]>([]);

  const isPrime = (n: bigint): boolean => {
    if (n < BigInt(2)) return false;
    if (n === BigInt(2)) return true;
    if (n % BigInt(2) === BigInt(0)) return false;
    
    const sqrt = BigInt(Math.floor(Math.sqrt(Number(n))));
    for (let i = BigInt(3); i <= sqrt; i += BigInt(2)) {
      if (n % i === BigInt(0)) return false;
    }
    return true;
  };

  const isPerfectSquare = (n: bigint): boolean => {
    if (n < BigInt(0)) return false;
    const sqrt = BigInt(Math.floor(Math.sqrt(Number(n))));
    return sqrt * sqrt === n;
  };

  const digitalRoot = (n: bigint): number => {
    let sum = n;
    while (sum >= BigInt(10)) {
      let temp = BigInt(0);
      while (sum > BigInt(0)) {
        temp += sum % BigInt(10);
        sum = sum / BigInt(10);
      }
      sum = temp;
    }
    return Number(sum);
  };

  const sumOfDigits = (n: bigint): number => {
    let sum = BigInt(0);
    let temp = n;
    while (temp > BigInt(0)) {
      sum += temp % BigInt(10);
      temp = temp / BigInt(10);
    }
    return Number(sum);
  };

  const getFibonacciProperties = (num: string): FibonacciProperties => {
    const bigNum = BigInt(num);
    return {
      isPrime: isPrime(bigNum),
      isEven: bigNum % BigInt(2) === BigInt(0),
      isPerfectSquare: isPerfectSquare(bigNum),
      digitalRoot: digitalRoot(bigNum),
      sumOfDigits: sumOfDigits(bigNum)
    };
  };

  const generateFibonacci = useCallback((): FibonacciResult | null => {
    const startTime = performance.now();
    
    try {
      const startA = BigInt(customStart ? startValues.a : '0');
      const startB = BigInt(customStart ? startValues.b : '1');
      const sequence: string[] = [];
      const ratios: number[] = [];
      const properties: string[] = [];

      let a = startA;
      let b = startB;

      if (generationMethod === 'count') {
        const num = parseInt(count);
        if (isNaN(num) || num <= 0 || num > 1000) {
          return null;
        }

        if (num >= 1) sequence.push(a.toString());
        if (num >= 2) sequence.push(b.toString());

        for (let i = 2; i < num; i++) {
          const next = a + b;
          sequence.push(next.toString());
          
          // Calculate golden ratio approximation
          if (b > BigInt(0)) {
            ratios.push(Number(next) / Number(b));
          }
          
          a = b;
          b = next;
        }
      } else if (generationMethod === 'max-value') {
        const maxVal = BigInt(maxValue);
        if (maxValue === '' || maxVal <= BigInt(0)) {
          return null;
        }

        if (a <= maxVal) sequence.push(a.toString());
        if (b <= maxVal) sequence.push(b.toString());

        while (true) {
          const next = a + b;
          if (next > maxVal) break;
          
          sequence.push(next.toString());
          
          if (b > BigInt(0)) {
            ratios.push(Number(next) / Number(b));
          }
          
          a = b;
          b = next;
          
          if (sequence.length > 1000) break; // Safety limit
        }
      } else if (generationMethod === 'nth-term') {
        const n = parseInt(count);
        if (isNaN(n) || n <= 0 || n > 1000) {
          return null;
        }

        // Generate up to nth term to show context
        const contextCount = Math.min(n + 5, 20);
        
        if (contextCount >= 1) sequence.push(a.toString());
        if (contextCount >= 2) sequence.push(b.toString());

        for (let i = 2; i < contextCount; i++) {
          const next = a + b;
          sequence.push(next.toString());
          a = b;
          b = next;
        }

        // Highlight the nth term
        properties.push(`The ${n}th Fibonacci number is: ${sequence[n - 1] || 'Not calculated'}`);
      }

      // Add mathematical properties for the sequence
      if (showProperties && sequence.length > 0) {
        const lastNum = sequence[sequence.length - 1];
        const props = getFibonacciProperties(lastNum);
        
        properties.push(`Last number (${lastNum}):`);
        properties.push(`• Is Prime: ${props.isPrime ? 'Yes' : 'No'}`);
        properties.push(`• Is Even: ${props.isEven ? 'Yes' : 'No'}`);
        properties.push(`• Is Perfect Square: ${props.isPerfectSquare ? 'Yes' : 'No'}`);
        properties.push(`• Digital Root: ${props.digitalRoot}`);
        properties.push(`• Sum of Digits: ${props.sumOfDigits}`);
        
        // Sequence properties
        const evenCount = sequence.filter(num => BigInt(num) % BigInt(2) === BigInt(0)).length;
        const oddCount = sequence.length - evenCount;
        properties.push(`Sequence Statistics:`);
        properties.push(`• Total Numbers: ${sequence.length}`);
        properties.push(`• Even Numbers: ${evenCount}`);
        properties.push(`• Odd Numbers: ${oddCount}`);
        
        if (sequence.length >= 2) {
          const lastRatio = Number(BigInt(sequence[sequence.length - 1])) / Number(BigInt(sequence[sequence.length - 2]));
          properties.push(`• Last Ratio: ${lastRatio.toFixed(6)}`);
          properties.push(`• Golden Ratio: 1.618033988749...`);
          properties.push(`• Difference: ${Math.abs(lastRatio - 1.618033988749).toFixed(6)}`);
        }
      }

      const endTime = performance.now();
      const processingTime = endTime - startTime;

      return {
        sequence,
        method: generationMethod,
        count: sequence.length,
        processingTime,
        timestamp: new Date(),
        ratios: showRatios ? ratios : undefined,
        properties: properties.length > 0 ? properties : undefined
      };
    } catch (error) {
      return null;
    }
  }, [count, maxValue, generationMethod, startValues, customStart, showProperties, showRatios]);

  const performGeneration = useCallback((): void => {
    const fibResult = generateFibonacci();
    if (fibResult) {
      setResult(fibResult);
      setHistory(prev => [fibResult, ...prev.slice(0, 9)]); // Keep last 10 generations
      
      const largestInSequence = fibResult.sequence[fibResult.sequence.length - 1];
      
      setStats(prev => ({
        sequencesGenerated: prev.sequencesGenerated + 1,
        largestNumber: BigInt(largestInSequence) > BigInt(prev.largestNumber) ? largestInSequence : prev.largestNumber,
        totalTime: prev.totalTime + fibResult.processingTime,
        averageTime: (prev.totalTime + fibResult.processingTime) / (prev.sequencesGenerated + 1),
        lastMethod: fibResult.method
      }));
    } else {
      setResult(null);
    }
  }, [generateFibonacci]);

  useEffect(() => {
    if ((generationMethod === 'count' && count) || (generationMethod === 'max-value' && maxValue) || generationMethod === 'nth-term') {
      performGeneration();
    }
  }, [count, maxValue, generationMethod, startValues, customStart, showProperties, showRatios, performGeneration]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    let textToCopy = `Fibonacci Sequence (${result.method}):\n`;
    textToCopy += result.sequence.join(', ');
    
    if (result.properties) {
      textToCopy += '\n\nProperties:\n' + result.properties.join('\n');
    }
    
    if (result.ratios && result.ratios.length > 0) {
      textToCopy += '\n\nRatios:\n' + result.ratios.map(r => r.toFixed(6)).join(', ');
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Sequence copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadSequence = (): void => {
    if (!result) return;
    
    let content = `Fibonacci Sequence Report\n`;
    content += `Generated: ${result.timestamp.toLocaleString()}\n`;
    content += `Method: ${result.method}\n`;
    content += `Count: ${result.count} numbers\n`;
    content += `Processing Time: ${result.processingTime.toFixed(2)}ms\n\n`;
    
    content += `Sequence:\n${result.sequence.join(', ')}\n\n`;
    
    if (result.properties) {
      content += `Properties:\n${result.properties.join('\n')}\n\n`;
    }
    
    if (result.ratios && result.ratios.length > 0) {
      content += `Ratios (approaching Golden Ratio):\n`;
      result.ratios.forEach((ratio, index) => {
        content += `F(${index + 3})/F(${index + 2}) = ${ratio.toFixed(6)}\n`;
      });
      content += `\n`;
    }
    
    content += `Statistics:\n`;
    content += `- Total Sequences Generated: ${stats.sequencesGenerated}\n`;
    content += `- Largest Number Ever: ${stats.largestNumber}\n`;
    content += `- Average Generation Time: ${stats.averageTime.toFixed(2)}ms\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fibonacci-sequence.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Sequence downloaded!');
  };

  const clearAll = (): void => {
    setCount('10');
    setMaxValue('');
    setStartValues({ a: '0', b: '1' });
    setCustomStart(false);
    setShowProperties(false);
    setShowRatios(false);
    setResult(null);
    setHistory([]);
    setStats({
      sequencesGenerated: 0,
      largestNumber: '0',
      totalTime: 0,
      averageTime: 0,
      lastMethod: ''
    });
    showNotification('Generator reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleGeneration = (): void => {
    setGenerationMethod('count');
    setCount('20');
    setShowProperties(true);
    setShowRatios(true);
    showNotification('Sample generation loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-orange-900 to-yellow-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Fibonacci Generator</h1>
              <p className="text-gray-300 text-sm">Advanced Fibonacci sequence generator with mathematical analysis</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Generation Method Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Generation Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setGenerationMethod('count')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'count'
                  ? 'border-orange-500 bg-orange-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Generate N Terms</div>
              <div className="text-sm opacity-75">Generate first N Fibonacci numbers</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('max-value')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'max-value'
                  ? 'border-orange-500 bg-orange-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Up to Max Value</div>
              <div className="text-sm opacity-75">Generate until reaching max value</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('nth-term')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'nth-term'
                  ? 'border-orange-500 bg-orange-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Find Nth Term</div>
              <div className="text-sm opacity-75">Calculate specific Fibonacci number</div>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Generation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.sequencesGenerated}</div>
                <div className="text-gray-300 text-sm">Sequences</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-green-400 break-all">
                  {stats.largestNumber.length > 10 ? 
                    `${stats.largestNumber.substring(0, 8)}...` : 
                    stats.largestNumber
                  }
                </div>
                <div className="text-gray-300 text-sm">Largest Number</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.lastMethod.replace('-', ' ')}</div>
                <div className="text-gray-300 text-sm">Last Method</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{history.length}</div>
                <div className="text-gray-300 text-sm">History Items</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Parameters</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleGeneration}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              {generationMethod === 'count' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Number of Terms (1-1000)
                  </label>
                  <input
                    type="number"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    min="1"
                    max="1000"
                    placeholder="Enter count"
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                  />
                </div>
              )}
              
              {generationMethod === 'max-value' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Maximum Value
                  </label>
                  <input
                    type="text"
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    placeholder="Enter maximum value"
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                  />
                </div>
              )}
              
              {generationMethod === 'nth-term' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nth Term (1-1000)
                  </label>
                  <input
                    type="number"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    min="1"
                    max="1000"
                    placeholder="Enter term number"
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                  />
                </div>
              )}

              <div className="border-t border-gray-600 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-300">
                    Custom Starting Values
                  </label>
                  <button
                    onClick={() => setCustomStart(!customStart)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      customStart ? 'bg-orange-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {customStart ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                {customStart && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">First (F0)</label>
                      <input
                        type="text"
                        value={startValues.a}
                        onChange={(e) => setStartValues(prev => ({ ...prev, a: e.target.value }))}
                        placeholder="0"
                        className="w-full bg-gray-900 text-white p-2 rounded border border-gray-600 focus:border-orange-500 outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Second (F1)</label>
                      <input
                        type="text"
                        value={startValues.b}
                        onChange={(e) => setStartValues(prev => ({ ...prev, b: e.target.value }))}
                        placeholder="1"
                        className="w-full bg-gray-900 text-white p-2 rounded border border-gray-600 focus:border-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-600 pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Show Mathematical Properties
                  </label>
                  <button
                    onClick={() => setShowProperties(!showProperties)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showProperties ? 'bg-orange-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showProperties ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Show Golden Ratio Convergence
                  </label>
                  <button
                    onClick={() => setShowRatios(!showRatios)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showRatios ? 'bg-orange-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showRatios ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Generated Sequence</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadSequence}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[500px]">
              {result ? (
                <div className="space-y-4">
                  <div className="text-center border-b border-gray-600 pb-4">
                    <div className="text-2xl font-bold text-orange-400 mb-2">
                      {result.count} Numbers Generated
                    </div>
                    <div className="text-gray-400 text-sm">
                      Method: {result.method.replace('-', ' ')} | 
                      Time: {result.processingTime.toFixed(2)}ms |
                      Generated: {result.timestamp.toLocaleString()}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-2">Fibonacci Sequence:</h4>
                    <div className="bg-gray-700 p-3 rounded font-mono text-sm text-green-400 max-h-40 overflow-y-auto">
                      {result.sequence.join(', ')}
                    </div>
                  </div>
                  
                  {result.ratios && result.ratios.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Golden Ratio Convergence:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm max-h-32 overflow-y-auto">
                        {result.ratios.map((ratio, index) => (
                          <div key={index} className="text-yellow-400 font-mono">
                            F({index + 3})/F({index + 2}) = {ratio.toFixed(6)}
                          </div>
                        ))}
                        <div className="text-gray-400 mt-2 text-xs">
                          Golden Ratio (φ) ≈ 1.618033988749...
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.properties && result.properties.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Mathematical Properties:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1 max-h-40 overflow-y-auto">
                        {result.properties.map((prop, index) => (
                          <div key={index} className={`${prop.startsWith('•') ? 'text-blue-400 ml-4' : 'text-white font-medium'}`}>
                            {prop}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter parameters to generate Fibonacci sequence</p>
                    <p className="text-sm mt-2">Choose generation method and set values above</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Generations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-40 overflow-y-auto">
              {history.slice(0, 6).map((gen, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-white">
                      {gen.method.replace('-', ' ')} ({gen.count} terms)
                    </div>
                    <div className="text-xs text-gray-400">
                      {gen.processingTime.toFixed(1)}ms
                    </div>
                  </div>
                  <div className="text-xs font-mono text-green-400 truncate">
                    {gen.sequence.slice(0, 8).join(', ')}
                    {gen.sequence.length > 8 ? '...' : ''}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {gen.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mathematical Information */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">About Fibonacci Sequence</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-orange-400 mb-2">Definition</h4>
              <p className="text-gray-300">
                F(n) = F(n-1) + F(n-2)<br/>
                Starting with F(0) = 0, F(1) = 1<br/>
                Sequence: 0, 1, 1, 2, 3, 5, 8, 13, 21, 34...
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-yellow-400 mb-2">Golden Ratio</h4>
              <p className="text-gray-300">
                φ = (1 + √5) / 2 ≈ 1.618033988749...<br/>
                lim(n→∞) F(n+1)/F(n) = φ<br/>
                The ratio of consecutive terms approaches φ
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-green-400 mb-2">Properties</h4>
              <p className="text-gray-300">
                • Every 3rd number is even<br/>
                • Every 4th number is divisible by 3<br/>
                • Every 5th number is divisible by 5<br/>
                • GCD(F(m), F(n)) = F(GCD(m, n))
              </p>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FibonacciGeneratorTool;